import React from 'react'
// @src imports
import {
  Button,
  DefaultError,
  FloatingDrawer,
  Icon,
  Transition,
} from 'src/chrome'
import { getPaperTypes } from 'src/app/constants'
import VariationToggle from 'src/editor/components/VariationToggle/VariationToggle'
import { getTotal } from 'src/helpers'
import {
  CardPaperType,
  CardType,
  CreateCardVariables,
  DetailedSendableCardFragment,
  getCard,
  getSendableCard,
  SendableCardVariationFragment,
  SendableCardWithTemplateFragment,
} from 'src/graphql'
import { CardPanelView } from 'src/orders/components'
import {
  CardFlip,
  CardTypeOptions,
  PaperTypeOptions,
} from 'src/catalog/components'
import { CardOption } from 'src/catalog/components/CardOptions/CardOptions'
// relative imports
import styles from './cardPreview.module.scss'
import {
  getRouteCardType,
  getRoutePaperType,
} from '../../../redux/selectors/catalog'
import {
  useActions,
  useAppcues,
  useCallback,
  useEffect,
  useMemo,
  useMutations,
  useQueries,
  useSelector,
  useState,
} from 'src/hooks'
import * as Result from 'src/utils/Result'
import suspenseBoundary from 'src/chrome/SuspenseBoundary/suspenseBoundaryHOC'
import { FlexRow } from 'src/styled'
import { Div, Spacer, Text } from '@sendoutcards/quantum-design-ui'

const cardTypes: CardOption<CardType>[] = [
  {
    value: CardType.FLATCARD,
    label: 'Flat Card',
    icon: 'FLATPANEL',
    iconSize: 30,
    viewBox: '0 0 25 32',
  },
  {
    value: CardType.TWO_PANEL,
    label: '2-Panel',
    icon: 'STANDARDCARD',
    iconSize: 30,
    viewBox: '0 0 18 32',
  },
  {
    value: CardType.THREE_PANEL,
    label: '3-Panel',
    icon: 'TRIFOLD',
    iconSize: 30,
    viewBox: '0 0 27 32',
  },
  {
    value: CardType.TWO_PANEL_BIG,
    label: 'Big Card',
    icon: 'BIGCARD',
    iconSize: 30,
    viewBox: '0 0 41 32',
    upgrade: '$1.50',
  },
  {
    value: CardType.POSTCARD,
    label: 'Post Card',
    icon: 'POSTCARD',
    iconSize: 30,
    viewBox: '0 0 48 32',
  },
]

const getVariations = (
  sendableCard: DetailedSendableCardFragment,
): SendableCardWithTemplateFragment[] => {
  return [sendableCard, ...sendableCard.variations]
}

const getSelectedVariation = (
  sendableCard: DetailedSendableCardFragment,
  variationId?: string,
): SendableCardVariationFragment =>
  (variationId &&
    getVariations(sendableCard).find(
      variation => variation.id === variationId,
    )) ||
  sendableCard

const isDefined = <T extends {} | undefined>(value: T) =>
  typeof value !== 'undefined'

interface Props {
  onClose: () => void
  onSelect?: (id: string) => void
  type: 'SendableCard' | 'Card'
  id: string
}

const CardPreview: React.FC<Props> = props => {
  const { onClose, onSelect, type, id: cardId } = props

  const routeCardType = useSelector(getRouteCardType)
  const routePaperType = useSelector(getRoutePaperType)
  const isMobile = useSelector(state => state.window.isMobile)

  const [card] = useQueries(
    type === 'SendableCard'
      ? getSendableCard({ id: cardId })
      : getCard({ id: cardId }),
  )

  const [variationId, setVariationId] = useState<string | undefined>(undefined)

  const [isCardSelected, setIsCardSelected] = useState(
    isDefined(routeCardType) || isDefined(routePaperType),
  )

  const [cardType, setCardType] = useState(routeCardType ?? CardType.TWO_PANEL)
  const [isCardFlipped, setIsCardFlipped] = useState(false)

  const [paperType, setPaperType] = useState(
    routePaperType ?? CardPaperType.STD,
  )

  const [isCustomizeOpen, setIsCustomizeOpen] = useState(
    isDefined(routeCardType) || isDefined(routePaperType),
  )

  const { order } = useSelector(state => state.orders)
  const actions = useActions()
  const mutations = useMutations()

  const getCardTypes = useMemo(() => {
    if (order?.lines.length === 1 && order.lines[0].giftVariation) {
      return cardTypes.filter(type => type.value !== 'POSTCARD')
    }

    return cardTypes
  }, [order])

  const paperUpgradeCost = useCallback(
    (maybeCardType?: CardType) => {
      const type = maybeCardType ?? cardType
      return type === CardType.TWO_PANEL_BIG ? 150 : 50
    },
    [cardType],
  )

  const cost = card.cost

  const isFree =
    cost &&
    (cost.total as Array<{ amount: number }>).reduce(
      (sum, price) => sum + price.amount,
      0,
    ) === 0

  const getPrice = useMemo(() => {
    // We only need this until point prices go up.
    if (!cost) {
      return undefined
    }

    const total = getTotal(cost)
    const pointsDiscount = total.indexOf('3 points') > -1 ? '5 points' : ''

    return (
      <>
        <Div outset={{ top: isMobile ? 'x_5' : 'x3' }}>
          <span className={styles.price}>
            <Div>
              <Div display="inline-block">
                <Text type="caption">PRICE</Text>
              </Div>
            </Div>
          </span>
          {pointsDiscount && (
            <Div display="inline-block">
              <Text
                type="subtitle"
                weight="bold"
                isStrikeThrough={true}
                outset={{ right: 'x1' }}
              >
                {`${pointsDiscount}`}
              </Text>
            </Div>
          )}
          <Div display="inline-block">
            <Text type="subtitle">
              {`${pointsDiscount && ' '}${getTotal(cost)}${
                pointsDiscount && ' -'
              }`}
            </Text>
          </Div>
          {pointsDiscount && (
            <>
              <Text type="body" color="primaryHeading">
                discount ends Jan 1
              </Text>
            </>
          )}
          <Div display="inline-block">
            <Text
              type="footnote"
              outset={{ left: pointsDiscount ? 'x0' : 'x1' }}
            >
              + plus postage
            </Text>
          </Div>
        </Div>
      </>
    )
  }, [cost, isMobile])

  const formattedPaperUpgrade = useMemo(() => {
    const paperCost: number = paperUpgradeCost()
    const paperCostString = paperCost.toString()

    return paperCost < 100
      ? `${paperCost}¢`
      : `$${paperCostString.slice(0, 1)}.${paperCostString.slice(1)}`
  }, [paperUpgradeCost])

  const paperTypes = useMemo(() => {
    return getPaperTypes(cardType).map(type => ({
      ...type,
      upgrade: type.upgrade ? formattedPaperUpgrade : type.upgrade,
    }))
  }, [cardType, formattedPaperUpgrade])

  useAppcues(isCardSelected ? '-LB2LReuyJSgtvsgG-06' : '-LBXiFzIGUsyytnR23-s')

  const handleSelectCard = useCallback(() => {
    setIsCardSelected(!isCardSelected)
    setIsCustomizeOpen(!isCustomizeOpen)
  }, [isCardSelected, isCustomizeOpen])

  useEffect(() => {
    handleSetPaperType(
      // Postcards may only have standard paper type
      cardType === CardType.POSTCARD ? CardPaperType.STD : paperType,
    )
  }, [cardType, paperType])

  const handleSetCardType = (value: CardType) => {
    setCardType(value)
  }

  const handleSetPaperType = (value: CardPaperType) => {
    setPaperType(value)
  }

  const handleCardFlip = () => {
    setIsCardFlipped(!isCardFlipped)
  }

  const handleVariation = (variation: SendableCardVariationFragment) => {
    setVariationId(variation.id)
  }

  const variations =
    card.__typename === 'SendableCard' ? getVariations(card) : undefined
  const selectedVariation =
    card.__typename === 'SendableCard'
      ? getSelectedVariation(card, variationId)
      : undefined
  const mergedSendableCard: DetailedSendableCardFragment | undefined =
    card.__typename === 'SendableCard' && selectedVariation
      ? {
          ...card,
          ...selectedVariation,
          description: card.description,
          title: card.title,
        }
      : undefined

  const orientation = (card || mergedSendableCard || { isHorizontal: false })
    .isHorizontal
    ? 'horizontal'
    : 'portrait'

  const cardSizing: string = (
    card ||
    mergedSendableCard || { isHorizontal: false }
  ).isHorizontal
    ? styles.landscape
    : styles.portrait

  const createCard = async ({
    card,
    sendableCard,
    type,
    paperType,
  }: CreateCardVariables) => {
    try {
      const { createCard } = await mutations.createCard({
        card,
        sendableCard,
        type,
        paperType,
      })
      actions.loadedUser(Result.success(createCard.account))
      if (onSelect) {
        onSelect(createCard.card.id)
        return
      }
      actions.openCard(createCard.card.id)
    } catch (error) {
      console.log(error)
    }
  }

  const handleEditCard = (
    id: string,
    cardType: CardType,
    paperType: CardPaperType,
  ) => {
    onClose()
    createCard({
      sendableCard: id,
      type: cardType,
      paperType,
    })
  }

  const handleEditCustomCard = (id: string) => actions.openCard(id)

  const handleSendCustomCard = () => {
    onClose()
    createCard({ card: cardId })
  }

  const handleFavoriteCard = async (id: string, isFavorite: boolean) => {
    try {
      if (isFavorite) {
        await mutations.unfavoriteCard({ id })
      } else {
        await mutations.favoriteCard({ id })
      }
    } catch (error) {
      console.error('Failed to favorite/unfavorite card.')
    }
  }

  return (
    <div className={styles.cardPreview}>
      <div
        className={
          orientation === 'horizontal'
            ? `${styles.cardModal} ${styles.horizontalCard}`
            : orientation === 'portrait'
            ? `${styles.cardModal} ${styles.portraitCard}`
            : styles.cardModal
        }
      >
        <div
          className={
            isCustomizeOpen
              ? `${styles.previewContainer} ${styles.configurations}`
              : styles.previewContainer
          }
          id={'preview_container'}
        >
          {isMobile && <h4 style={{ textAlign: 'center' }}>Card Preview</h4>}
          <div className={styles.card}>
            {card.__typename === 'SendableCard' && (
              <CardFlip
                isFlipped={isCardFlipped}
                card={mergedSendableCard ?? card}
                isButtonEnabled={false}
                customCardSize={cardSizing}
                handleFavoriteCard={handleFavoriteCard}
              />
            )}
            {card.__typename === 'Card' && <CardPanelView card={card} />}
            <div
              style={
                {
                  position: 'absolute',
                  bottom: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                } as React.CSSProperties
              }
            >
              {selectedVariation && variations && variations.length > 1 && (
                <VariationToggle
                  style={{
                    bottom: 0,
                    boxShadow: '0px 2px 12px 0 #595959',
                    background: '#FFF',
                  }}
                  className={
                    orientation === 'horizontal'
                      ? `${styles.catalogVariation} ${styles.horizontalVariation}`
                      : `${styles.catalogVariation} ${styles.portraitVariation}`
                  }
                  variations={variations}
                  selectedVariationId={selectedVariation.id}
                  selectVariation={handleVariation}
                />
              )}
            </div>
          </div>
          <div className={styles.cardDetails}>
            <div className={styles.closePreview} onClick={() => onClose()}>
              <Icon icon={'CLOSE'} size={18} />
              <Text type="caption" color="primaryHeading">
                Close
              </Text>
            </div>
            <div className={styles.nonMobile}>
              {!isCardSelected && (
                <div className={styles.details}>
                  <Text type="title">
                    {mergedSendableCard?.title ?? 'Custom Card'}
                  </Text>
                  {mergedSendableCard?.isPremium && (
                    <div className={styles.premiumTag}>
                      <Icon icon={'TAG'} size={18} color={'#F171CC'} />
                      <Div display="inline-block">
                        <Text
                          type="caption"
                          weight="bold"
                          color="primaryHeading"
                        >
                          Premium Card
                        </Text>
                      </Div>
                    </div>
                  )}
                  <Spacer space="x2" />
                  <Text type="caption" color="primaryHeading">
                    {mergedSendableCard?.description ?? ''}
                  </Text>
                  {!isFree && (
                    <FlexRow style={{ alignItems: 'flex-end' }}>
                      {getPrice}
                    </FlexRow>
                  )}
                  {isFree && 'FREE'}
                </div>
              )}
              {type === 'Card' && (
                <div className={styles.actions}>
                  <div
                    className={`${styles.action} ${styles.cardFlip}`}
                    onClick={() => handleEditCustomCard(card.id)}
                    id={'edit_and_save_custom_card_btn'}
                  >
                    Edit and Save
                  </div>
                  <div
                    className={`${styles.action} ${styles.chooseCard}`}
                    onClick={handleSendCustomCard}
                    id={'custom_card_to_editor_btn'}
                  >
                    Send Card
                  </div>
                </div>
              )}
            </div>
            {mergedSendableCard && (
              <div
                className={styles.actions}
                style={
                  isMobile
                    ? {
                        position: 'fixed',
                        bottom: '0px',
                        left: '0px',
                        right: '0px',
                      }
                    : { marginBottom: '25%' }
                }
              >
                {mergedSendableCard.insideRightImage && (
                  <Button
                    title={isCardFlipped ? 'See Front' : 'See Inside'}
                    id={isCardFlipped ? 'see_front_btn' : 'see_inside_btn'}
                    onClick={handleCardFlip}
                  />
                )}
                <FloatingDrawer
                  isOpen={isCustomizeOpen}
                  triggerTitle={'Customize'}
                  triggerCloseTitle={'Cancel'}
                  onClick={handleSelectCard}
                  className={styles.action}
                >
                  {isCardSelected && (
                    <div className={styles.options}>
                      <Div inset="x2" backgroundColor="white">
                        <Text type="title" alignment="center">
                          {!['3666591', '3666592'].includes(
                            mergedSendableCard ? mergedSendableCard.id : '',
                          )
                            ? 'Customize Card Options'
                            : 'Enjoy the creative process!'}
                        </Text>
                      </Div>
                      <Spacer space="x2" />
                      <CardTypeOptions
                        options={getCardTypes}
                        optionTitle={'Pick a card type:'}
                        selectedOption={cardType}
                        selectOption={handleSetCardType}
                      />
                      <PaperTypeOptions
                        options={paperTypes}
                        optionTitle={'Choose Paper Type:'}
                        selectedOption={paperType}
                        selectOption={handleSetPaperType}
                      />
                      <div className={styles.actionButton}>
                        <div
                          className={`${styles.action} ${styles.chooseCard}`}
                          id={'card_preview_personalize_btn'}
                          onClick={() => {
                            const id = selectedVariation?.id ?? card?.id
                            if (id) {
                              handleEditCard(id, cardType, paperType)
                            }
                          }}
                        >
                          <Text
                            type="largeBody"
                            color="accent"
                            alignment="center"
                            weight="bold"
                          >
                            Next
                          </Text>
                        </div>
                      </div>
                    </div>
                  )}
                </FloatingDrawer>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.modalWindow} onClick={() => onClose()} />
    </div>
  )
}

export default suspenseBoundary({
  component: CardPreview,
  unresolved: (
    <Transition
      message={'Loading your selection...'}
      messageStyle={{ color: 'black', fontWeight: 500 }}
    />
  ),
  failure: DefaultError,
})
